/**
 * Account Page
 *
 * Unified account management page with tabbed sections:
 * 1. Account Information - Personal info + Partner info
 * 2. Therapy & Background - Therapy style, relationship context, custom instructions
 * 3. Usage - User's usage statistics
 * 4. Billing - Subscription management
 *
 * Supports URL-based tab selection via ?tab= parameter for deep linking
 * (e.g., after Stripe checkout redirect to /account?tab=billing).
 */

import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { Layout } from "../components/Layout";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { AccountInfoSection } from "../components/account/AccountInfoSection";
import { TherapyBackgroundSection } from "../components/account/TherapyBackgroundSection";
import { UsageSection } from "../components/account/UsageSection";
import { BillingSection } from "../components/account/BillingSection";
import { User, Heart, BarChart3, CreditCard } from "lucide-react";

type TabValue = "account" | "therapy" | "usage" | "billing";

const VALID_TABS: TabValue[] = ["account", "therapy", "usage", "billing"];

function isValidTab(tab: string | null): tab is TabValue {
  return tab !== null && VALID_TABS.includes(tab as TabValue);
}

export function AccountPage() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get tab from URL or default to "account"
  const tabParam = searchParams.get("tab");
  const initialTab = isValidTab(tabParam) ? tabParam : "account";
  const [activeTab, setActiveTab] = useState<TabValue>(initialTab);

  // Check if redirected due to incomplete profile
  const locationState = location.state as {
    profileIncomplete?: boolean;
    returnTo?: string;
  } | null;
  const profileIncomplete = locationState?.profileIncomplete ?? false;
  const returnTo = locationState?.returnTo;

  // Sync tab with URL param
  useEffect(() => {
    const tabFromUrl = searchParams.get("tab");
    if (isValidTab(tabFromUrl) && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams, activeTab]);

  // Update URL when tab changes
  const handleTabChange = (value: string) => {
    const newTab = value as TabValue;
    setActiveTab(newTab);

    // Update URL params, preserving other params like success/canceled
    const newParams = new URLSearchParams(searchParams);
    newParams.set("tab", newTab);
    setSearchParams(newParams, { replace: true });
  };

  return (
    <Layout>
      <div className="container mx-auto max-w-6xl px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Account</h1>
          <p className="text-muted-foreground">
            Manage your account settings, preferences, and subscription.
          </p>
        </div>

        <Tabs
          value={activeTab}
          onValueChange={handleTabChange}
          className="w-full"
        >
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="account" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              <span className="hidden sm:inline">Account</span>
            </TabsTrigger>
            <TabsTrigger value="therapy" className="flex items-center gap-2">
              <Heart className="h-4 w-4" />
              <span className="hidden sm:inline">Therapy</span>
            </TabsTrigger>
            <TabsTrigger value="usage" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              <span className="hidden sm:inline">Usage</span>
            </TabsTrigger>
            <TabsTrigger value="billing" className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              <span className="hidden sm:inline">Billing</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="account">
            <AccountInfoSection
              profileIncomplete={profileIncomplete}
              returnTo={returnTo}
            />
          </TabsContent>

          <TabsContent value="therapy">
            <TherapyBackgroundSection />
          </TabsContent>

          <TabsContent value="usage">
            <UsageSection />
          </TabsContent>

          <TabsContent value="billing">
            <BillingSection />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
